// =============================================================================
// Card Component - Professional Investor Portal Design
// =============================================================================

import { forwardRef, HTMLAttributes } from 'react';
import clsx from 'clsx';

// =============================================================================
// Types
// =============================================================================

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'elevated' | 'outlined' | 'ghost';
}

// =============================================================================
// Component
// =============================================================================

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, hover = false, padding = 'md', variant = 'default', children, ...props }, ref) => {
    const paddings = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    };

    const variants = {
      default: 'bg-white border border-gray-200/60 shadow-card',
      elevated: 'bg-white shadow-elevated',
      outlined: 'bg-white border border-gray-200',
      ghost: 'bg-gray-50/50',
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'rounded-xl transition-all duration-200',
          variants[variant],
          hover && 'hover:shadow-card-hover hover:border-gray-300/60 cursor-pointer',
          paddings[padding],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// =============================================================================
// Card Header
// =============================================================================

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, subtitle, action, size = 'md', ...props }, ref) => {
    const titleSizes = {
      sm: 'text-sm font-semibold',
      md: 'text-base font-semibold',
      lg: 'text-lg font-semibold',
    };

    return (
      <div
        ref={ref}
        className={clsx('flex items-start justify-between', className)}
        {...props}
      >
        <div>
          <h3 className={clsx(titleSizes[size], 'text-gray-900')}>{title}</h3>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
        {action && <div className="flex-shrink-0 ml-4">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// =============================================================================
// Card Content
// =============================================================================

export const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={clsx('mt-4', className)} {...props}>
      {children}
    </div>
  );
});

CardContent.displayName = 'CardContent';

// =============================================================================
// Card Footer
// =============================================================================

export const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx(
        'mt-4 pt-4 border-t border-gray-100 flex items-center justify-end gap-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

CardFooter.displayName = 'CardFooter';

// =============================================================================
// Metric Card - For dashboard statistics
// =============================================================================

export interface MetricCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
  };
  icon?: React.ReactNode;
  trend?: string;
}

export const MetricCard = forwardRef<HTMLDivElement, MetricCardProps>(
  ({ className, label, value, change, icon, trend, ...props }, ref) => {
    return (
      <Card ref={ref} className={className} padding="md" {...props}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-500 truncate">{label}</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900 tracking-tight">
              {value}
            </p>
            {(change || trend) && (
              <div className="mt-2 flex items-center gap-2">
                {change && (
                  <span
                    className={clsx(
                      'inline-flex items-center text-xs font-medium',
                      change.type === 'increase' && 'text-success-600',
                      change.type === 'decrease' && 'text-danger-600',
                      change.type === 'neutral' && 'text-gray-500'
                    )}
                  >
                    {change.type === 'increase' && '↑'}
                    {change.type === 'decrease' && '↓'}
                    {change.value > 0 ? '+' : ''}{change.value}%
                  </span>
                )}
                {trend && (
                  <span className="text-xs text-gray-400">{trend}</span>
                )}
              </div>
            )}
          </div>
          {icon && (
            <div className="flex-shrink-0 p-3 bg-primary-50 rounded-lg">
              {icon}
            </div>
          )}
        </div>
      </Card>
    );
  }
);

MetricCard.displayName = 'MetricCard';

export default Card;
